import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import Seo from '../components/Seo.jsx'
import { useToast } from '../context/ToastContext.jsx'
import { submitFeedback } from '../lib/feedback.js'

/*
 * /feedback — a short form for written feedback. Submissions land in the feedback
 * table and are emailed to the site owner by scripts/send-feedback.mjs.
 */
export default function Feedback() {
  const { pathname } = useLocation()
  const toast = useToast()
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (message.trim().length < 2) return toast('Please add a short message.', 'red')
    setBusy(true)
    const res = await submitFeedback({ message, email, rating: rating || null, path: pathname })
    setBusy(false)
    if (res?.error) return toast(res.error, 'red')
    setSent(true)
  }

  return (
    <main className="page">
      <Seo
        title="Feedback | CalculateMyPeptides"
        description="Tell us what's working, what's confusing, or a feature you'd like us to add — your feedback on the peptide calculator goes straight to the team. Bugs, ideas and requests all welcome."
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Feedback', path: '/feedback' }]}
      />
      <div className="container page-narrow">
        <span className="pill blue" style={{ marginBottom: 8 }}>We're listening</span>
        <h1 className="section-title">Send feedback</h1>
        <p className="section-sub" style={{ maxWidth: '58ch' }}>
          Found a bug, something confusing, or a feature you wish we had? Tell us — it goes straight to the team.
        </p>

        {sent ? (
          <div className="card empty" style={{ marginTop: 10 }}>
            <div className="emoji">🙏</div>
            <h3 style={{ marginBottom: 6 }}>Thank you!</h3>
            <p style={{ marginBottom: 0 }}>We read every note. {email.trim() ? 'We’ll reply if it needs one.' : ''}</p>
          </div>
        ) : (
          <form className="card card-pad" onSubmit={submit} style={{ marginTop: 10 }}>
            <div className="field">
              <label className="label">How would you rate it? <span className="hint">(optional)</span></label>
              <div className="rate-stars" role="radiogroup" aria-label="Rate 1 to 5 stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" className={`rate-star${(hover || rating) >= n ? ' on' : ''}`}
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                    onFocus={() => setHover(n)} onBlur={() => setHover(0)}
                    onClick={() => setRating(n === rating ? 0 : n)}>★</button>
                ))}
              </div>
            </div>

            <div className="field">
              <label className="label">Your feedback</label>
              <textarea className="input" rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="What's working, what's not, or what to add…" style={{ resize: 'vertical' }} />
            </div>

            <div className="field mb-0">
              <label className="label">Email <span className="hint">(optional — only if you want a reply)</span></label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" />
            </div>

            <div className="row" style={{ marginTop: 16 }}>
              <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send feedback'}</button>
            </div>
            <p className="muted small" style={{ marginTop: 12, marginBottom: 0 }}>
              We won’t share your email, and we only use it to reply to you.
            </p>
          </form>
        )}
      </div>
    </main>
  )
}
